<script>
import UsersMixin from "@/views/users/mixins/UsersMixin.vue";
import BaseView from "@/mixins/BaseView.vue";
import { useFiltersCache } from "@/composables";
import SearchField from "@/components/inputs/SearchField.vue";
import TrucksMixin from "@/views/fleets/mixins/TrucksMixin.vue";
import DatePicker from "@/components/pickers/datePicker/DatePicker.vue";
import UserSelect from "@/views/users/components/UserSelect.vue";
import TableFilter from "@/components/tables/TableFilter.vue";
import TableFilterRow from "@/components/tables/TableFilterRow.vue";
import CitizenshipSelect from "@/components/selects/CitizenshipSelect.vue";
import OmniSelect from "@/components/selects/OmniSelect.vue";
import TruckStatusSelect from "@/components/statuses/TruckStatusSelect.vue";
import DispatcherSelect from "@/components/selects/DispatcherSelect.vue";
import TruckExclusiveIcon from "@/components/icons/TruckExclusiveIcon.vue";
import { licensePlateMask, licenseStateMask, phoneMask, vinCodeMask, yearFromMask } from "@/constants/mask.ts";
import { useValidation } from "@/composables/useValidation.ts";

const searchParamsKey = "trucks";

const { validateVIN, licensePlateValidator, yearFromValidator, licenseStateValidator } = useValidation();
const { getSearchParams, setSearchParams } = useFiltersCache();

export default {
  name: "TrucksListSearchForm",
  components: {
    DispatcherSelect,
    OmniSelect,
    TruckStatusSelect,
    CitizenshipSelect,
    TableFilterRow,
    TableFilter,
    DatePicker,
    SearchField,
    UserSelect,
    TruckExclusiveIcon,
  },
  mixins: [BaseView, UsersMixin, TrucksMixin],
  emits: ["search"],
  data() {
    return {
      params: {},
      showAdditionalFilters: false,
    };
  },
  computed: {
    licenseStateMask() {
      return licenseStateMask;
    },
    yearFromMask() {
      return yearFromMask;
    },
    licensePlateMask() {
      return licensePlateMask;
    },
    vinCodeMask() {
      return vinCodeMask;
    },
    phoneMask() {
      return phoneMask;
    },
  },
  created() {
    this.params = getSearchParams(searchParamsKey);
    this.search();
  },
  methods: {
    clear() {
      this.params = {};
      this.search();
    },
    search() {
      if (this.params.phone) {
        this.params.phone = this.params.phone.replaceAll(/\D/g, "").replaceAll(" ", "");
      }
      if (this.params.year && this.params.year === "") {
        delete this.params.year;
      }

      setSearchParams(searchParamsKey, this.params);
      this.$emit("search", this.params);
    },
    vinValidation(value) {
      return validateVIN(value);
    },
    licensePlateValidation(value) {
      return licensePlateValidator(value);
    },
    licenseStateValidation(value) {
      return licenseStateValidator(value);
    },
    yearFromValidation(value) {
      return yearFromValidator(value);
    },
  },
};
</script>

<template>
  <table-filter
    :filters="params"
    :header-filter-keys="['number', 'type', 'status', 'phone']"
    @apply="search"
    @clear="clear"
  >
    <template #header>
      <table-filter-row :divider="false" :label="$t('general.filter-main')" icon="mdi-filter-variant">
        <v-col md="2">
          <search-field v-model="params.number" :label="$t('general.number')" data-qa="number" @search="search" />
        </v-col>
        <v-col md="4">
          <omni-select
            v-model="params.type"
            :clearable="true"
            :items="truckTypes"
            :label="$t('general.type')"
            :multiple="true"
            color="primary"
            data-qa="truck-type"
            density="compact"
            hide-details
            item-title="name"
            item-value="id"
            variant="outlined"
            @search="search"
          />
        </v-col>
        <v-col md="2">
          <truck-status-select
            v-model="params.status"
            :label="$t('general.status')"
            clearable
            data-qa="truck-status"
            @search="search"
          />
        </v-col>
        <v-col>
          <search-field
            v-model="params.phone"
            :label="$t('general.phone')"
            :mask="phoneMask"
            data-qa="truck-phone"
            @search="search"
          />
        </v-col>
      </table-filter-row>
    </template>
    <table-filter-row :label="$t('trucks.details')" icon="mdi-truck-outline">
      <v-col cols="3">
        <search-field
          v-model="params.vin"
          :label="$t('trucks.vin')"
          :mask="vinCodeMask"
          :rules="[vinValidation]"
          data-qa="truck-vin"
          @search="search"
        />
      </v-col>
      <v-col cols="2">
        <dispatcher-select
          v-model="params.reservedBy"
          :label="$t('trucks.reserved')"
          data-qa="truck-reserved-by"
          @update:model-value="search"
        />
      </v-col>
      <v-col cols="2">
        <dispatcher-select
          v-model="params.hiredBy"
          :label="$t('trucks.hired')"
          data-qa="truck-hired-by"
          @update:model-value="search"
        />
      </v-col>
      <v-col :style="{ flex: '0 0 100px' }">
        <v-checkbox
          v-model="params.team"
          :label="$t('trucks.team')"
          color="primary"
          data-qa="truck-team"
          density="compact"
          hide-details
        />
      </v-col>
      <v-col :style="{ flex: '0 0 100px' }">
        <v-checkbox
          v-model="params.signs"
          :label="$t('trucks.signs')"
          color="primary"
          data-qa="truck-signs"
          density="compact"
          hide-details
        />
      </v-col>
      <v-col :style="{ flex: '0 0 100px' }">
        <v-checkbox v-model="params.exclusive" color="primary" data-qa="truck-exclusive" density="compact" hide-details>
          <template #label>
            <truck-exclusive-icon :size="20" hide-tooltip />
            <span>{{ $t("general.exclusive") }}</span>
          </template>
        </v-checkbox>
      </v-col>
    </table-filter-row>
    <table-filter-row :label="$t('trucks.registration-info')" icon="mdi-badge-account-horizontal-outline">
      <v-col>
        <search-field
          v-model="params.licencePlate"
          :label="$t('trucks.license-plate')"
          :mask="licensePlateMask"
          :rules="[licensePlateValidation]"
          data-qa="truck-license-plate"
          @search="search"
        />
      </v-col>
      <v-col>
        <search-field
          v-model="params.licenceState"
          :label="$t('trucks.license-state')"
          :mask="licenseStateMask"
          :rules="[licenseStateValidation]"
          data-qa="truck-license-state"
          @search="search"
        />
      </v-col>

      <v-col>
        <date-picker
          v-model="params.licenceExpirationDateFrom"
          :label="$t('trucks.license-expiration-from')"
          data-qa="truck-license-expiration-from"
        />
      </v-col>

      <v-col>
        <date-picker
          v-model="params.licenceExpirationDateTo"
          :label="$t('trucks.license-expiration')"
          data-qa="truck-license-expiration-to"
        />
      </v-col>
    </table-filter-row>
    <table-filter-row :label="$t('trucks.trailer-details')" icon="mdi-truck-trailer">
      <v-col class="dimension-part">
        <search-field
          v-model="params.length"
          :label="$t('trucks.length')"
          data-qa="truck-length"
          type="number"
          @search="search"
        />
      </v-col>
      <v-col class="dimension-part">
        <search-field
          v-model="params.width"
          :label="$t('trucks.width')"
          data-qa="truck-width"
          type="number"
          @search="search"
        />
      </v-col>
      <v-col>
        <search-field
          v-model="params.height"
          :label="$t('trucks.height')"
          data-qa="truck-height"
          type="number"
          @search="search"
        />
      </v-col>
      <v-col>
        <search-field
          v-model="params.year"
          :mask="yearFromMask"
          :rules="[yearFromValidation]"
          data-qa="truck-year-from"
          label="Year from"
          type="number"
          @search="search"
        />
      </v-col>
      <v-col>
        <search-field
          v-model="params.payload"
          :label="$t('trucks.payload')"
          data-qa="truck-payload"
          type="number"
          @search="search"
        />
      </v-col>
      <v-col>
        <omni-select
          v-model="params.equipment"
          :clearable="true"
          :items="equipment"
          :label="$t('trucks.equipment')"
          :multiple="true"
          data-qa="truck-equipment"
          item-title="name"
          item-value="id"
          @search="search"
        />
      </v-col>
      <v-col>
        <omni-select
          v-model="params.additionalEquipment"
          :clearable="true"
          :items="additionalEquipment"
          :label="$t('trucks.equipment-extra')"
          :multiple="true"
          data-qa="truck-additional-equipment"
          item-title="name"
          item-value="id"
          @search="search"
        />
      </v-col>
    </table-filter-row>
    <table-filter-row :divider="false" :label="$t('trucks.owner-driver')" icon="mdi-account-multiple-outline">
      <v-col>
        <user-select v-model="params.owner" :label="$t('users.owner')" data-qa="truck-owner" owners @change="search" />
      </v-col>
      <v-col>
        <user-select
          v-model="params.driver"
          :label="$t('users.driver')"
          data-qa="truck-driver"
          drivers
          @change="search"
        />
      </v-col>
      <v-col>
        <omni-select
          v-model="params.driverCertificate"
          :clearable="true"
          :items="driverCertificates"
          :label="$t('drivers.certificates')"
          color="primary"
          data-qa="truck-driver-certificates"
          density="compact"
          hide-details
          item-title="name"
          item-value="id"
          variant="outlined"
          @update:model-value="search"
        />
      </v-col>
      <v-col>
        <citizenship-select v-model="params.driverCitizenship" :clearable="true" @update:model-value="search" />
      </v-col>
      <v-col>
        <omni-select
          v-model="params.willingCrossBorder"
          :clearable="true"
          :items="driverWillingsToCrossBorder"
          :label="$t('drivers.willing-cross-border')"
          color="primary"
          data-qa="truck-willing-cross-border"
          density="compact"
          hide-details
          item-title="name"
          item-value="id"
          variant="outlined"
          @update:model-value="search"
        />
      </v-col>
    </table-filter-row>
  </table-filter>
</template>

<style lang="scss" scoped>
.dimension-part {
  position: relative;

  &:after {
    content: "х";
    position: absolute;
    top: 50%;
    right: -4px;
    transform: translateY(-50%);
  }
}
</style>
